import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { filterJobRoles } from "../../jobRoles.jsx";

// Matches SetDetailsCard.jsx's field styling exactly (behavioral's own builder
// screen) and SharedStep1_JobDetails.jsx's (the real job-creation flow) — pill
// inputs, floating label, violet border — rather than the glass-effect/gradient-
// ring style borrowed from MCQ's AI flow, which read as a different app mid-journey
// once Step 2 hands off into this same builder.
const inputCls =
  "w-full px-6 py-3 bg-transparent border border-[#5A4479] rounded-full dark:text-[#CCCCCC] text-textSecondary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5A4479] focus:border-transparent transition-all duration-200 disabled:opacity-50";

// 2 competencies x 3 questions x 1.5 min/question — the shortest duration the
// backend's own floor (behavioral-test-phase2-ai-guided-proposal.md §4) can satisfy.
export const MIN_DURATION_MINUTES = 9;
// A behavioural round is one part of a longer test sequence — 60 min keeps a
// single AI-generated round from silently eating a candidate's whole time budget.
export const MAX_DURATION_MINUTES = 60;
const DURATION_PRESETS = [10, 15, 20, 30];

// Flat violet fill + gradient-ring border (MCQ's own "is-active" selected-chip
// treatment, AIStep2_Topics.jsx) instead of a gradient fill — a fill's color stops
// are relative to the element's own width, so the same gradient class renders as a
// different color on a wide chip vs. a narrow one. A thin border doesn't have that
// problem. `gradient-ring` alone shows on hover; `is-active` forces it on.
const selectedChipCls = "is-active border-transparent bg-[#5A4479]/20 dark:text-white text-textPrimary";
const unselectedChipCls = "border-[#5A4479]/50 dark:text-gray-400 text-textSecondary hover:border-[#5A4479]";

// Same gradient-filled numbered dot MCQ's "How it works" panel uses
// (AIStep1_Configure.jsx), reused as this step's section marker.
const StepNumber = ({ n }) => (
  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-blue-500 flex items-center justify-center text-xs font-bold text-white">
    {n}
  </span>
);

const SectionHeader = ({ n, title, subtitle, required }) => (
  <div className="flex items-start gap-3">
    <StepNumber n={n} />
    <div>
      <h3 className="text-sm font-semibold dark:text-white text-textPrimary">
        {title} {required && <span className="text-red-400">*</span>}
      </h3>
      {subtitle && (
        <p className="text-xs dark:text-gray-400 text-textSecondary mt-0.5">{subtitle}</p>
      )}
    </div>
  </div>
);

/** One combined input for "who is this test for" — types a free-text role (with
 * static-list suggestions, same source RoleAutocomplete used) or picks one of the
 * org's real job descriptions, merged into a single suggestion list instead of a
 * separate tab + full list-browser (proposal's original two-mode picker). Typing
 * always sets source="role" immediately; picking a JD row sets source="jd" and
 * displays that JD's role name. The chevron opens the full JD list even with an
 * empty box, for "pick one of your existing job roles" without typing anything. */
const RoleOrJdAutocomplete = ({
  source, setSource,
  jobRole, setJobRole,
  jdId, setJdId,
  jdList, isJdListLoading,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [isOpen]);

  // Rendered text is always derived from source/jobRole/jdId — never its own
  // separate copy — so there's exactly one place ("who is this role") that can
  // ever disagree with what's shown.
  const selectedJd = source === "jd" ? jdList.find((jd) => jd.jd_id === jdId) : null;
  const displayValue = source === "jd" ? (selectedJd?.jobrole || "") : jobRole;

  const roleSuggestions = useMemo(
    () => (source === "role" ? filterJobRoles(jobRole, 6) : []),
    [source, jobRole]
  );
  const jdSuggestions = useMemo(() => {
    const needle = displayValue.trim().toLowerCase();
    const matches = needle
      ? jdList.filter((jd) => (jd.jobrole || "").toLowerCase().includes(needle))
      : jdList;
    return matches.slice(0, 8);
  }, [jdList, displayValue]);

  const showDropdown = isOpen && (roleSuggestions.length > 0 || jdSuggestions.length > 0 || isJdListLoading);

  const pickRole = (role) => {
    setSource("role");
    setJobRole(role);
    setJdId(null);
    setIsOpen(false);
  };
  const pickJd = (jd) => {
    setSource("jd");
    setJdId(jd.jd_id);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        placeholder="Type a role, or pick from your job roles"
        value={displayValue}
        onChange={(e) => { setSource("role"); setJdId(null); setJobRole(e.target.value); setIsOpen(true); }}
        onFocus={() => setIsOpen(true)}
        autoComplete="off"
        className={`${inputCls} pr-11`}
      />
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        tabIndex={-1}
        className="absolute right-4 top-1/2 -translate-y-1/2 dark:text-gray-500 text-textSecondary"
      >
        <ChevronDown size={16} />
      </button>

      {showDropdown && (
        <ul className="absolute z-10 w-full mt-1 dark:bg-gray-800 bg-bgPrimary border border-[#5A4479] rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {isJdListLoading && (
            <li className="px-4 py-2 dark:text-gray-500 text-textSecondary">Loading job descriptions…</li>
          )}
          {roleSuggestions.map((role) => (
            <li
              key={`role_${role}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickRole(role)}
              className="px-4 py-2 dark:text-[#CCCCCC] text-textSecondary cursor-pointer dark:hover:bg-gray-700 hover:bg-gray-200"
            >
              {role}
            </li>
          ))}
          {jdSuggestions.map((jd) => (
            <li
              key={`jd_${jd.jd_id}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickJd(jd)}
              className="px-4 py-2 dark:text-[#CCCCCC] text-textSecondary cursor-pointer dark:hover:bg-gray-700 hover:bg-gray-200"
            >
              {jd.jobrole || "Untitled role"}
            </li>
          ))}
          {!isJdListLoading && !roleSuggestions.length && !jdSuggestions.length && (
            <li className="px-4 py-2 dark:text-gray-500 text-textSecondary">No matches</li>
          )}
        </ul>
      )}
    </div>
  );
};

const BehavioralAIStep1_Requirements = ({
  source, setSource,          // "role" | "jd"
  jobRole, setJobRole,
  jdId, setJdId,
  jdList, isJdListLoading,
  timeBudget, setTimeBudget,
  testName, setTestName,
  onNext,
  onBack,
  isLoading,
}) => {
  const [customDuration, setCustomDuration] = useState("");
  const [errors, setErrors] = useState({ requirement: "", duration: "" });

  const validate = () => {
    const next = { requirement: "", duration: "" };
    if (source === "role" && !jobRole.trim()) next.requirement = "Enter a job role, or pick one of your job descriptions";
    if (source === "jd" && !jdId) next.requirement = "Select a job description";
    if (!timeBudget || timeBudget < MIN_DURATION_MINUTES)
      next.duration = `Shortest realistic test is ${MIN_DURATION_MINUTES} minutes (2 competencies × 3 questions)`;
    else if (timeBudget > MAX_DURATION_MINUTES)
      next.duration = `Keep a single behavioural round to ${MAX_DURATION_MINUTES} minutes or under`;
    setErrors(next);
    return !next.requirement && !next.duration;
  };

  const handleNext = () => { if (validate()) onNext(); };

  return (
    <div className="w-full space-y-8">
      <h1 className="text-lg font-light dark:text-white text-textPrimary">
        Tell us what you need. AI will design a role-specific behavioural set for you.
      </h1>

      <div className="space-y-3">
        <SectionHeader n="1" title="Who is this test for?" required
          subtitle="Type any role, or pick one of your existing job roles as you type." />
        <div className="pl-9 max-w-xl">
          <RoleOrJdAutocomplete
            source={source} setSource={setSource}
            jobRole={jobRole} setJobRole={setJobRole}
            jdId={jdId} setJdId={setJdId}
            jdList={jdList} isJdListLoading={isJdListLoading}
          />
          {errors.requirement && <p className="text-red-400 text-sm mt-1.5">{errors.requirement}</p>}
        </div>
      </div>

      <div className="space-y-3">
        <SectionHeader n="2" title="How long should the test take?" required
          subtitle="Sets how many questions we generate." />
        <div className="pl-9 flex flex-wrap items-center gap-2">
          {DURATION_PRESETS.map((mins) => (
            <button
              key={mins}
              type="button"
              onClick={() => { setTimeBudget(mins); setCustomDuration(""); setErrors((p) => ({ ...p, duration: "" })); }}
              className={`gradient-ring px-4 py-2.5 rounded-full text-sm font-medium border transition-colors ${
                timeBudget === mins && !customDuration ? selectedChipCls : unselectedChipCls
              }`}
            >
              {mins} min
            </button>
          ))}
          <input
            type="number"
            min={MIN_DURATION_MINUTES}
            max={MAX_DURATION_MINUTES}
            placeholder="Custom"
            value={customDuration}
            onChange={(e) => {
              setCustomDuration(e.target.value);
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) { setTimeBudget(n); setErrors((p) => ({ ...p, duration: "" })); }
            }}
            className="bg-transparent border border-[#5A4479]/50 rounded-full w-24 px-4 py-2.5 text-sm dark:text-white text-textPrimary placeholder-gray-400 focus:outline-none focus:border-[#5A4479] transition-colors"
          />
        </div>
        {errors.duration && <p className="text-red-400 text-sm pl-9">{errors.duration}</p>}
      </div>

      <div className="space-y-3">
        <SectionHeader n="3" title="Set name"
          subtitle="Defaults to the role name if left blank. You can rename it later." />
        <div className="pl-9 max-w-xl">
          <input
            type="text"
            placeholder="e.g. Senior Backend Engineer Behavioural Set"
            value={testName}
            onChange={(e) => setTestName(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <div className="flex justify-between items-center w-full pt-4">
        <button
          onClick={onBack}
          className="border-btn dark:border-btn-dark px-6 py-3 rounded-full text-sm font-medium transition-all"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={isLoading}
          className="button px-6 py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Getting recommendation...</span>
            </>
          ) : (
            <span>Recommend →</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default BehavioralAIStep1_Requirements;
